// RentTracker.tsx - Rent management dashboard component
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/firebaseAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Tenancy } from '../../types/tenancyTypes';
import {
  ExclamationTriangleIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import BackHeader from '../common/BackHeader';
import MarkPaidModal from './MarkPaidModal';
import PropertyList from './PropertyList';
import TenancyCard from './TenancyCard';
import PropertyActionBar from './PropertyActionBar';

import { formatCurrency } from '../../utility/formatters';
import BroadcastModal from '../messages/BroadcastModal';

import { ManagedProperty as BaseManagedProperty } from '../property/property-card/PropertyCardTypes';
type ManagedProperty = BaseManagedProperty & {
  firestoreDocId: string;
};

interface RentTrackerProps {
  className?: string;
}

const RentTracker: React.FC<RentTrackerProps> = ({ className = '' }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [properties, setProperties] = useState<ManagedProperty[]>([]);
  const [selectedProperty, setSelectedProperty] =
    useState<ManagedProperty | null>(null);
  const [tenancies, setTenancies] = useState<Tenancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenancy, setSelectedTenancy] = useState<Tenancy | null>(null);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<
    'cash' | 'bank-transfer' | 'other'
  >('bank-transfer');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // Fetch managed properties on component mount
  useEffect(() => {
    const fetchProperties = async () => {
      if (!currentUser) return;

      setLoading(true);
      setError(null);

      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          'https://europe-west1-rentsmart-6e31b.cloudfunctions.net/getManagedProperties',
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch managed properties');
        }

        const data = await response.json();
        setProperties(data.data || []);
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError('Failed to load properties. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [currentUser]);

  const fetchPropertyTenants = useCallback(
    async (propertyId: number) => {
      setLoading(true);
      setError(null);

      try {
        if (!currentUser) return;
        const token = await currentUser.getIdToken();

        const response = await fetch(
          `https://europe-west1-rentsmart-6e31b.cloudfunctions.net/getTenantsForProperty?listingId=${propertyId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 404) {
          setTenancies([]);
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch tenants');
        }

        const data = await response.json();
        setTenancies(data.data || []);
      } catch (err) {
        console.error('Error fetching property tenants:', err);
        setError('Failed to load tenants. Please try again.');
        toast.error('Could not load tenants');
      } finally {
        setLoading(false);
      }
    },
    [currentUser]
  );

  // Handle deep linking from ManagedPropertyPortfolio
  useEffect(() => {
    const state = location.state as { propertyId?: number } | null;
    if (state?.propertyId && properties.length > 0 && !selectedProperty) {
      const targetProperty = properties.find((p) => p.id === state.propertyId);
      if (targetProperty) {
        setSelectedProperty(targetProperty);
        fetchPropertyTenants(targetProperty.id);
        // Clear state to prevent re-selection on navigation
        navigate('.', { replace: true, state: {} });
      }
    }
  }, [
    properties,
    location.state,
    selectedProperty,
    fetchPropertyTenants,
    navigate
  ]);

  const handleMarkAsPaid = async () => {
    if (!selectedTenancy || !currentUser) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setProcessingPayment(true);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        'https://europe-west1-rentsmart-6e31b.cloudfunctions.net/markRentAsPaid',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            tenancyId: selectedTenancy.id,
            amountPaid: amount,
            paymentMethod,
            notes: paymentNotes
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mark rent as paid');
      }

      const data = await response.json();

      // Update the tenancy in local state immediately
      setTenancies((prevTenancies) =>
        prevTenancies.map((t) => {
          if (t.id === selectedTenancy.id) {
            return {
              ...t,
              arrears: data.data.arrears,
              currentPeriod: {
                ...t.currentPeriod,
                paymentStatus: data.data.paymentStatus,
                amountPaid: data.data.totalPaid
              }
            };
          }
          return t;
        })
      );

      // Reset and close modal
      setShowMarkPaidModal(false);
      setSelectedTenancy(null);
      setPaymentAmount('');
      setPaymentNotes('');

      toast.success(
        `Payment of ${formatCurrency(amount)} recorded successfully!`
      );
    } catch (err) {
      console.error('Error marking rent as paid:', err);
      toast.error(
        err instanceof Error
          ? err.message
          : 'Failed to record payment. Please try again.'
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSendBroadcast = async (
    subjectArg?: string,
    messageArg?: string,
    targetTenancyId?: string
  ) => {
    if (
      !currentUser ||
      !selectedProperty ||
      !(subjectArg ?? broadcastSubject) ||
      !(messageArg ?? broadcastMessage)
    ) {
      toast.error('Please fill in both subject and message');
      return;
    }

    setSendingBroadcast(true);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        'https://europe-west1-rentsmart-6e31b.cloudfunctions.net/sendBroadcastToTenants',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            listingId: selectedProperty.id,
            tenancyId: targetTenancyId,
            subject: subjectArg ?? broadcastSubject,
            message: messageArg ?? broadcastMessage
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send broadcast');
      }

      const data = await response.json();

      // Reset and close modal
      setShowBroadcastModal(false);
      setBroadcastSubject('');
      setBroadcastMessage('');

      toast.success(
        `Broadcast sent successfully to ${data.data.recipientCount} tenant(s)!`
      );
    } catch (err) {
      console.error('Error sending broadcast:', err);
      toast.error(
        err instanceof Error
          ? err.message
          : 'Failed to send broadcast. Please try again.'
      );
    } finally {
      setSendingBroadcast(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="text-center text-red-600">
          <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-2" />
          <p>{error}</p>
          <button
            onClick={() => {
              if (selectedProperty) {
                fetchPropertyTenants(selectedProperty.id);
              } else {
                window.location.reload();
              }
            }}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show property list view
  if (!selectedProperty) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <BackHeader
          title="Your Properties"
          onBack={() => navigate('/landlord-toolkit')}
        />

        <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg text-blue-800">
          <p className="text-sm">
            Select a property below to track rent payments, manage arrears, and
            communicate with tenants.
          </p>
        </div>

        <PropertyList
          properties={properties}
          onSelect={(property) => {
            setSelectedProperty(property);
            fetchPropertyTenants(property.id);
          }}
        />
      </div>
    );
  }

  // Show tenant list view for selected property
  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <PropertyActionBar
        property={{
          title: selectedProperty.title,
          address: selectedProperty.address,
          location: selectedProperty.location
        }}
        hasTenancies={tenancies.length > 0}
        onBack={() => {
          setSelectedProperty(null);
          setTenancies([]);
        }}
        onSendBroadcast={() => setShowBroadcastModal(true)}
      />

      {tenancies.length === 0 ? (
        <div className="text-center py-12">
          <UsersIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Tenants Yet
          </h3>
          <p className="text-gray-600 mb-6">
            This property doesn't have any active tenants.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {tenancies.map((tenancy) => (
            <TenancyCard
              key={tenancy.id}
              tenancy={tenancy}
              onSendReminder={() => {
                setSelectedProperty(selectedProperty); // ensure context
                setBroadcastSubject('Rent Payment Reminder');
                setShowBroadcastModal(true);
              }}
              onMarkPaid={(t) => {
                setSelectedTenancy(t);
                setPaymentAmount(
                  (t.rentAmount - t.currentPeriod.amountPaid).toString()
                );
                setShowMarkPaidModal(true);
              }}
              onSubTenantsChanged={() => {
                if (selectedProperty) {
                  fetchPropertyTenants(selectedProperty.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Mark as Paid Modal */}
      <MarkPaidModal
        isOpen={!!(showMarkPaidModal && selectedTenancy)}
        propertyTitle={
          selectedTenancy ? selectedTenancy.propertyInfo.title : ''
        }
        tenantName={selectedTenancy ? selectedTenancy.tenantInfo.name : ''}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        paymentMethod={paymentMethod}
        setPaymentMethod={(v) => setPaymentMethod(v)}
        paymentNotes={paymentNotes}
        setPaymentNotes={setPaymentNotes}
        arrears={selectedTenancy ? selectedTenancy.arrears || 0 : 0}
        currentBalance={
          selectedTenancy
            ? selectedTenancy.rentAmount -
              selectedTenancy.currentPeriod.amountPaid
            : 0
        }
        totalToClear={
          selectedTenancy
            ? selectedTenancy.rentAmount -
              selectedTenancy.currentPeriod.amountPaid +
              (selectedTenancy.arrears || 0)
            : 0
        }
        onCancel={() => {
          setShowMarkPaidModal(false);
          setSelectedTenancy(null);
          setPaymentAmount('');
          setPaymentNotes('');
        }}
        onConfirm={handleMarkAsPaid}
        processingPayment={processingPayment}
        formatCurrency={formatCurrency}
      />

      {/* Broadcast Modal */}
      <BroadcastModal
        isOpen={!!(showBroadcastModal && selectedProperty)}
        onClose={() => {
          setShowBroadcastModal(false);
          setBroadcastSubject('');
          setBroadcastMessage('');
        }}
        recipientName={selectedProperty.title}
        tenancies={tenancies
          .filter((t) => t.id)
          .map((t) => ({
            id: t.id as string,
            name: t.tenantInfo.name
          }))}
        initialSubject={broadcastSubject}
        onSend={(s, m, tid) => handleSendBroadcast(s, m, tid)}
        isSending={sendingBroadcast}
      />
    </div>
  );
};

export default RentTracker;
